import i18n from "i18next";
import moment from "moment";

// Set by useHome/UserSettings when user data loads; avoids importing store (circular dep)
let _userNumberFormatLocale = null;
export const setUserNumberFormatLocale = locale => {
  _userNumberFormatLocale = locale || null;
};

import {
  CHARGE_BALANCE_THRESHOLD,
  CHEMICALS_SYMBOL_MAPPING,
  DATE_FORMAT,
  SATURATION_INDEX_THRESHOLD,
  SATURATION_PERCENTAGE_THRESHOLD,
  TECHNOLOGIES,
  TIME_UNITS,
  USER_INFO,
} from "@constants/global.constants";

const featureIdToField = {
  1: "mCIP",
  2: "CEB",
};

export const getDisabledUFTech = ufSpecialFeatureID => featureIdToField[ufSpecialFeatureID] || "";

export const convertUpto2Digits = value => {
  if (value === TBD_VALUE) return TBD_VALUE;
  return isNaN(parseFloat(value)) ? "0" : parseFloat(value).toFixed(2);
};

export const isFiniteNumber = value => typeof value === "number" && Number.isFinite(value);
export const TBD_VALUE = "TBD";

/**
 * Rounds a finite number to 6 decimal places.
 * Returns the original value unchanged if it is not a finite number.
 */
export const roundToSixDigits = value => (Number.isFinite(value) ? Math.round(value * 1e6) / 1e6 : value);

export const convertUptoDigits = (value, digits = 2, isFixed, options = {}) => {
  if (value === TBD_VALUE) return TBD_VALUE;

  const { localize = false, locale = getActiveLocale() } = options;

  const tempValue = isNaN(value) ? 0 : parseFloat(value);
  const rounded = ((tempValue || isFixed) && tempValue.toFixed(digits)) || 0;

  if (!localize) return rounded;

  // Locale decimal separator only (no grouping, no precision change)
  return localizeDecimalSeparator(String(rounded), locale);
};

// Returns the user's chosen number-format locale, falling back to the app language locale
export const getActiveLocale = () => _userNumberFormatLocale || getLocale(i18n.language);

// Maps app language name or cultureName to a BCP 47 locale for number formatting
export const getLocale = (language = i18n.language) => {
  switch (language?.toLowerCase()) {
    case "spanish":
    case "es":
      return "es-ES";
    case "chinese":
    case "zh":
      return "zh-CN";
    default:
      return "en-US";
  }
};

/**
 * Formats a number for UI display using the given language's locale.
 * Uses grouping separators (thousands). For input display use convertUptoDigits with localize:true.
 * Never pass the return value to API, DB writes, or calc-engine — those always use raw JS numbers.
 */
export const formatNumberInLocale = (value, maxFractionDigits = 2, options = {}) => {
  if (value === null || value === undefined || value === "") return "";
  return new Intl.NumberFormat(getActiveLocale(), {
    minimumFractionDigits: maxFractionDigits,
    maximumFractionDigits: maxFractionDigits,
    ...options,
  }).format(Number(value));
};

export const formatIfNumber = value => {
  if (value === null || value === undefined || value === "") return value;
  const numericValue = Number(value);
  return Number.isNaN(numericValue)
    ? value
    : formatNumberInLocale(numericValue, 0, { minimumFractionDigits: 0, maximumFractionDigits: 20 });
};

/**
 * Changes the format of a chemical symbol based on a predefined mapping.
 * Replace each occurrence of the 'from' symbol with the 'to' symbol
 *
 * @param {string} chemicalSymbol - The chemical symbol to be formatted.
 * @return {string} - The formatted chemical symbol.
 */
export const changeChemicalFormat = chemicalSymbol => {
  if (!chemicalSymbol) return "0";

  for (const [from, to] of Object.entries(CHEMICALS_SYMBOL_MAPPING)) {
    chemicalSymbol = `${chemicalSymbol}`.replaceAll(from, to);
  }

  return chemicalSymbol;
};

/**
 * Converts a source object into a new object with keys appended by a type.
 * @param {Object} source - The source object.
 * @param {string} type - The type to append to each key.
 * @return {Object} - The new object with keys appended by the type.
 */
export const getChemicalObject = (source, type, disabledField) =>
  Object.entries(source).reduce((result, [key, value]) => {
    const paramValue = disabledField && key.includes(disabledField) ? "0" : value;
    result[`${key}_${type}`] = paramValue;
    return result;
  }, {});

export const getFolderName = foldername =>
  foldername?.length > 20 ? `${foldername?.substring(0, 20)}...` : foldername;

export const debounce = (callback, timeout = 500) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
    }, timeout);
  };
};

const validData = (data, label, flag) => {
  const den = flag ? "0" : 0;
  return data ? data[label] : den;
};

export const formatChemicalSymbol = (chemicalSymbol, flag, field = "symbol") =>
  changeChemicalFormat(validData(chemicalSymbol, field, flag)).toString();

export const calculateSum = (...args) => {
  let arr = args;
  // If the first argument is an array, use it; otherwise, use all arguments as array
  if (args.length === 1 && Array.isArray(args[0])) {
    arr = args[0];
  }
  return arr.reduce((acc, val) => {
    const value = parseFloat(val) || 0;
    return acc + value;
  }, 0);
};

/**
 * Calculates the difference between two dates.
 *
 * @param {string|Date|moment.Moment} date1 - The first date.
 * @param {string|Date|moment.Moment} date2 - The second date.
 * @returns {number} The difference between the two dates in milliseconds.
 */
export const getDateDiff = (date1, date2) => moment(date1).diff(moment(date2));

/**
 * Creates a comparator function for sorting objects based on a specified key, type, and order.
 *
 * @param {Object} options - The options for the comparator.
 * @param {string} options.key - The key of the object to sort by.
 * @param {string} options.type - The type of the value to sort ("string" or "date").
 * @param {string} [options.order="asc"] - The order of sorting ("asc" for ascending or "desc" for descending).
 * @param {boolean} [options.useNumeric] - Whether to use numeric collation for string comparison.
 * @returns {Function} A comparator function that can be used in array sort method.
 */
export const sortComparator =
  ({ key, type, order = "asc", useNumeric, aliasKeys }) =>
  (a, b) => {
    const getValue = obj => {
      if (obj[key] === undefined && isIterable(aliasKeys)) {
        for (const aliaskey of aliasKeys) {
          if (obj[aliaskey] !== undefined) {
            return obj[aliaskey];
          }
        }
      }
      return obj[key];
    };

    let valA = getValue(a, key, aliasKeys);
    let valB = getValue(b, key, aliasKeys);

    if (order === "desc") {
      // swapping the variables for desc order
      [valA, valB] = [valB, valA];
    }
    if (type === "string") {
      return valA?.localeCompare(valB, undefined, { numeric: useNumeric });
    } else if (type === "date") {
      // date should be in ISO Format
      return getDateDiff(valA, valB);
    }
    return valA - valB;
  };

/**
 * Multi-sort utility function using sortComparator
 * @param {Array} array - The array to sort
 * @param {Array} criteria - An array of sorting criteria follows sortComparator params
 * @returns {Array} - The sorted array
 */
export const multiSort = (array, criteria) =>
  array.sort((a, b) => {
    for (const criterion of criteria) {
      const result = sortComparator(criterion)(a, b);
      if (result !== 0) {
        return result; // If comparison is not equal, return the result
      }
    }
    return 0; // If all criteria are equal, return 0
  });

// function returns true if all doses are disabled or total dose value is 0
export const isDoseErr = ({ indicators, doseValues }) => {
  const isAllDosesDisabled = indicators.every(indicator => !indicator);
  const totalDoseValue = calculateSum(doseValues);
  return isAllDosesDisabled || totalDoseValue === 0;
};

export const loadExternalScript = url => {
  if (url) {
    const script = document.createElement("script");
    script.src = url;
    document.body.appendChild(script);
  }
};

export const isValueInRange = (value, ranges) => {
  if (isNaN(value) || !ranges) return false;
  const minValue = ranges.minValue !== undefined ? ranges.minValue : ranges.min;
  const maxValue = ranges.maxValue !== undefined ? ranges.maxValue : ranges.max;

  const val = parseFloat(value);
  return val >= minValue && val <= maxValue;
};

export const groupBy = (array, key) => {
  if (!array || !key) return {};
  return array.reduce((acc, currentValue) => {
    const groupKey = currentValue[key];
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(currentValue);
    return acc;
  }, {});
};

/**
 * Converts an array of objects into an object, using a specified key from each object as the key for the new object.
 *
 * @param {Array} array - The array of objects to convert.
 * @param {string} key - The key to use for the new object.
 * @returns {Object} - The resulting object with keys derived from the specified key in each object.
 */
export const convertListToObj = (array, key) => {
  if (!array || !key) return {};
  return array.reduce((acc, item) => ({ ...acc, [item[key]]: item }), {});
};

/**
 * Clones an array and overrides specific objects within it based on provided overrides.
 *
 * @param {Array<Object>} array - The original array to be cloned and modified.
 * @param {Object} overrides - An object containing keys that match the `arrayObjkey` values in the array and values that are objects with properties to override.
 * @param {string} arrayObjkey - The key in the array objects that will be used to match against the keys in the overrides object.
 * @returns {Array<Object>} - A new array with the specified overrides applied.
 */
export const cloneArrayAndOverride = (array, overrides, arrayObjkey) => {
  const clonedArray = structuredClone(array);
  Object.entries(overrides).forEach(([overrideValueKey, OverrideValueData]) => {
    const arrayOverrideIndex = clonedArray.findIndex(obj => obj[arrayObjkey] === overrideValueKey);
    if (arrayOverrideIndex !== -1) {
      clonedArray[arrayOverrideIndex] = {
        ...clonedArray[arrayOverrideIndex],
        ...OverrideValueData,
      };
    }
  });
  return clonedArray;
};

/**
 * Capitalizes the first character of the input string.
 *
 * @param {string} value - The input string to be capitalized.
 * @return {string} - The input string with the first character capitalized.
 */
export const capitalize = value => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Formats a date using the moment library.
 *
 * @param {string|Date} date - The date to be formatted.
 * @param {string} format - The format string to use.
 * @return {string} - The formatted date string.
 */
export const formatDate = (date, format = DATE_FORMAT) => (date ? moment(date).format(format) : "-");

export const getQuickProjectName = () => `Quick Project ${moment().format("DD-MMM-YYYY hh:mm:ss A")}`;

/**
 * Combines multiple class names into a single string.
 *
 * @param {...string} classes - The class names to combine.
 * @returns {string} A single string with all the class names combined, separated by spaces.
 */
export const combineClassNames = (...classes) => classes.filter(Boolean).join(" ").trim();

/**
 * Checks if a value is null or undefined.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} True if the value is null or undefined, false otherwise.
 */
export const isNullOrUndefined = value => value === null || value === undefined;

export const nodeIdHasTech = (nodeId, technology) =>
  typeof nodeId === "string" && Boolean(technology) && nodeId.includes(technology);

// MODE is the Vite-native way to detect dev mode (not driven by VITE_PROJECT_ENV)
export const isLocalEnv = () => import.meta.env.MODE === "development";
export const isProdEnv = () => import.meta.env.VITE_PROJECT_ENV?.toUpperCase() === "PROD";
export const isDevEnv = () => import.meta.env.VITE_PROJECT_ENV?.toUpperCase() === "DEV";
export const isTestEnv = () => import.meta.env.VITE_PROJECT_ENV?.toUpperCase() === "TEST";
export const isLowerEnv = () => isLocalEnv() || isDevEnv() || isTestEnv();
export const isSandboxEnv = () => import.meta.env.VITE_PROJECT_ENV?.toUpperCase() === "SANDBOX";

/**
 * Checks if the user is a DuPont user based on their email address.
 *
 * @param {string} email - The user's email address.
 * @returns {boolean} True if the email contains '@dupont.com', false otherwise.
 */
export const isDuPontUser = email => email?.toLowerCase().includes("@dupont.com") || false;

/**
 * Downloads the provided data as a file with the specified file name.
 *
 * This function creates a Blob from the provided data, generates a URL for the Blob,
 * creates an anchor element, sets its href to the Blob URL, and triggers a download
 * by programmatically clicking the anchor element. After a short delay, it removes
 * the anchor element and revokes the Blob URL to free up memory.
 *
 * @param {BlobPart} data - The data to be downloaded.
 * @param {string} fileName - The name of the file to be downloaded.
 */
export const downloadBlobData = (data, fileName) => {
  const blob = new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const aTag = document.createElement("a");
  aTag.href = url;
  aTag.download = fileName;
  document.body.appendChild(aTag);
  aTag.click();
  setTimeout(() => {
    aTag.remove();
    window.URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Swaps the positions of two elements in an array and updates their displayOrder properties.
 *
 * @param {Array} cases - The array of case objects.
 * @param {number} index1 - The index of the first element to swap.
 * @param {number} index2 - The index of the second element to swap.
 * @returns {Array} - A new array with the elements at index1 and index2 swapped and their displayOrder properties updated.
 */
export const swapData = (cases, index1, index2) => {
  const newCases = cases.map(caseItem => ({ ...caseItem }));
  [newCases[index1], newCases[index2]] = [newCases[index2], newCases[index1]];

  // Update displayOrder
  const tempDisplayOrder = newCases[index1].displayOrder;
  newCases[index1].displayOrder = newCases[index2].displayOrder;
  newCases[index2].displayOrder = tempDisplayOrder;

  return newCases;
};

/**
 * Memoizes a given function by caching its results.
 *
 * @param {Function} fn - The function to be memoized.
 * @returns {Function} - A new function that caches the results of the original function.
 *
 * The returned function takes any number of arguments and checks if the result for those arguments
 * is already in the cache. If it is, it returns the cached result. If not, it calls the original
 * function with the arguments, stores the result in the cache, and then returns the result.
 */
export const memoizedFun = fn => {
  const cache = {};
  return (...args) => {
    const key = args.toString();
    if (cache[key]) {
      return cache[key];
    }
    const result = fn(...args);
    cache[key] = result;
    return result;
  };
};

export const isEmptyObject = obj => !obj || Object.keys(obj).length === 0;

export const isPlainObject = obj => {
  if (typeof obj !== "object" || obj === null) return false;
  return Object.getPrototypeOf(obj) === Object.prototype;
};

export const isSelectedTreatment = (treatmentName, systemDesignCaseTreatmentVM) =>
  systemDesignCaseTreatmentVM?.find(item => item.treatmentName.includes(treatmentName));

export const truncateText = (text, length) => {
  if (text.length > length) {
    return `${text.substring(0, length)}...`;
  }
  return text;
};

/**
 * Truncates a floating-point number to a specified number of decimal places.
 *
 * @param {number} number - The number to be truncated.
 * @param {number} [decimalPlaces=2] - The number of decimal places to keep. Defaults to 2.
 * @returns {number} - The truncated number.
 *
 * e.g it will truncate 5.239999999 => 5.23, when decimalPlaces is 2 and 5.239, when it is 3
 */
export const truncateFloat = (number, decimalPlaces = 2) => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(number * factor) / factor;
};

export const getFileExtension = fileName => fileName.split(".").pop();

export const isValuesZero = (...values) => values.every(value => parseFloat(value) === 0);

export const isChargeBalApproxZero = chargeBal => {
  if (chargeBal === undefined) return false;

  const num = Math.abs(Number(chargeBal));

  return num <= CHARGE_BALANCE_THRESHOLD;
};

/**
 * Converts a snake_case string to camelCase.
 *
 * @param {string} str - The snake_case string to convert.
 * @return {string} - The converted camelCase string.
 */
export const snakeToCamelCase = (str = "") => str.replace(/(_\w)/g, matches => matches[1].toUpperCase());

export const injectValues = (str, value) => {
  Object.entries(value).forEach(([key, val]) => {
    const replaceStr = "${" + key + "}";
    str = str.replace(replaceStr, val);
  });
  return str;
};

export const getTrimedTextInLowerCase = text => text?.toLowerCase().trim();

export const getSupportedTechnology = technologyName => {
  if (technologyName === "" || technologyName === undefined) return null;
  return technologyName.split(",");
};

export const isArrayEqual = (a, b) => {
  if (a === null || a == undefined || b === null || b === undefined) return false;
  if (a?.length !== b?.length) return false;
  const tempArr1 = Array.isArray(a) ? [...a] : [];
  const tempArr2 = Array.isArray(b) ? [...b] : [];
  tempArr1.sort();
  tempArr2.sort();
  for (let i = 0; i < tempArr1.length; i++) {
    if (tempArr1[i] !== tempArr2[i]) return false;
  }
  return true;
};

/**
 * creating a helper function for deep comparision of two arrays or array of objects instead of using json.Stringify
 * @param {Object} object1 - The first object to compare.
 * @param {Object} object2 - The second object to compare.
 *  */
export const isArrayDataSame = (object1, object2) => {
  if (object1 === object2) return true;
  if (!object1 || !object2 || typeof object1 !== "object" || typeof object2 !== "object") return false;

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key) || !isArrayDataSame(object1[key], object2[key])) {
      return false;
    }
  }
  return true;
};

const getTimeDiffs = (time, formatter) => {
  const now = moment();
  time = moment(formatter(time, false));
  return Object.entries(TIME_UNITS).reduce((acc, [unit]) => ({ ...acc, [unit]: now.diff(time, unit) }), {});
};

/**
 * Returns a human-readable time difference based on the current time.
 *
 * @param {string|Date|moment.Moment} time - The time to compare with the current time.
 * @returns {string} - A human-readable time difference.
 */
export const getTimeDifference = (t, time, formatter, displayYears) => {
  const diff = getTimeDiffs(time, formatter);

  const getTimeString = (unit, type) => {
    const infoKey = unit > 1 ? `${type}sAgo` : `${type}Ago`;
    return t(infoKey).replace("${time}", unit);
  };

  if (diff.minutes < 1) {
    return t("justNow");
  } else if (diff.minutes < 60) {
    return getTimeString(diff.minutes, TIME_UNITS.minutes);
  } else if (diff.hours < 24) {
    return getTimeString(diff.hours, TIME_UNITS.hours);
  } else if (diff.days < 2 && !displayYears) {
    return getTimeString(1, TIME_UNITS.days);
  } else if (displayYears) {
    if (diff.years > 0) {
      return getTimeString(diff.years, TIME_UNITS.years);
    } else if (diff.months > 0) {
      return getTimeString(diff.months, TIME_UNITS.months);
    } else if (diff.weeks > 0) {
      return getTimeString(diff.weeks, TIME_UNITS.weeks);
    } else {
      return getTimeString(diff.days, TIME_UNITS.days);
    }
  } else {
    return formatter ? formatter(time) : moment(time).format(DATE_FORMAT);
  }
};

export const isIterable = obj => obj != null && typeof obj[Symbol.iterator] === "function";

/**
 * Converts a value to a number. Defaults to 0 if the value is invalid.
 *
 * @param {*} value - The value to convert.
 * @returns {number} - The converted number or 0.
 */
export const convertToNumber = value => {
  const num = Number(value ?? 0);
  return isNaN(num) ? 0 : num;
};

/**
 * Converts a value to a number or returns null if the value is empty or not finite.
 * @param {*} value - The value to convert
 * @returns {number|null} The numeric value or null if not a valid number
 */
export const toNumberOrNull = value => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const getFiles = () => {
  const currentLanguage = i18n.language || USER_INFO.cultureName;
  const modules = import.meta.glob("../locales/**/*.json", { eager: true });
  const files = Object.keys(modules).filter(fileName => fileName.includes(`/${currentLanguage}/`));
  return files.map(fileName => {
    const parts = fileName.split(`/${currentLanguage}/`);
    return parts[parts.length - 1];
  });
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
};

// Sums the values of two objects for matching keys: if both values are numbers, their sum is used; otherwise, the value from the first object is used.
export const sumTwoObjects = (obj1, obj2) =>
  Object.keys(obj1).reduce((result, key) => {
    const val1 = Number(obj1[key]);
    const val2 = Number(obj2[key]);
    result[key] = !isNaN(val1) && !isNaN(val2) ? val1 + val2 : obj1[key];
    return result;
  }, {});

// Returns a new object with only the specified keys from the input object.
export const getSubsetOfObject = (obj, keys) => {
  if (!obj || !keys || !Array.isArray(keys)) return {};
  return keys.reduce((result, key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
    return result;
  }, {});
};

// Returns the first key whose value exactly matches the provided value.
export const getObjectKeyByValue = (obj, value) => {
  if (!obj || typeof obj !== "object") return null;
  return Object.keys(obj).find(key => obj[key] === value) || null;
};

/**
 * Returns a new object with a nested field updated immutably.
 *
 * Follows a common set-style API: (object, path, value)
 * where path can be a dot-separated string or an array of keys.
 *
 * @param {Object|Array} obj - Base object.
 * @param {string|string[]} path - Dot path (e.g. "data.edgeFlow") or key array.
 * @param {*} value - Value to set at the path.
 * @returns {Object|Array} New object/array with updated value.
 */
export const updateNestedObjectField = (obj, path, value) => {
  const rawPath = Array.isArray(path) ? path : [path];
  const pathKeys = rawPath
    .flatMap(pathPart => `${pathPart ?? ""}`.split("."))
    .map(pathPart => pathPart.trim())
    .filter(Boolean);

  if (!pathKeys.length) return obj;

  const [currentKey, ...restKeys] = pathKeys;
  const base = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };

  if (!restKeys.length) {
    base[currentKey] = value;
    return base;
  }

  base[currentKey] = updateNestedObjectField(obj?.[currentKey], restKeys, value);
  return base;
};

/**
 * Reads a nested value from an object using an array of keys.
 *
 * @param {Object|Array} obj - Source object.
 * @param {string[]} path - Keys to follow, e.g. ["construction", "concDetail"].
 * @returns {*} The value at the path, or undefined when any key is missing.
 */
export const getNestedObjectField = (obj, path) => path.reduce((value, key) => value?.[key], obj);

export const extractNumber = sourceString => {
  const match = sourceString.match(/\d+$/); // Matches digits at the end of the string
  return match ? parseInt(match[0], 10) : null;
};

export const replaceArrayValuesAtIndex = (arr, indexObj) => arr.map((item, ind) => indexObj[ind] || item);

export const getErrorMessage = (error, errorKey) => {
  const code = error?.response?.data?.responseCode;
  if (code) {
    return `errorCode.${code}`;
  }
  return errorKey || "requestFailed";
};

/**
 * Formats a number to a given precision but removes trailing zeros after the decimal point,
 * keeping at least 'trailingZero' digits after the decimal.
 * @param {number|string} value - The value to format.
 * @param {number} digits - Number of decimal places to keep (max).
 * @param {number} trailingZero - Minimum number of digits to keep after the decimal.
 * @returns {string|number} - US-format string (always uses '.') — safe as input value prop, not for direct display.
 */
export const formatNumberNoTrailingZeros = (value, digits = 2, trailingZero = 2) => {
  const tempValue = isNaN(value) ? 0 : parseFloat(value);
  if (typeof tempValue === "number" && !isNaN(tempValue)) {
    const str = tempValue.toFixed(digits);
    if (str.indexOf(".") === -1) return str; // no decimal point
    // Remove trailing zeros but keep at least 'trailingZero' digits after decimal
    let [intPart, decPart] = str.split(".");
    if (decPart.length > trailingZero) {
      // Remove trailing zeros, but keep at least trailingZero digits
      let trimmed = decPart.replace(/0+$/, "");
      if (trimmed.length < trailingZero) {
        trimmed = decPart.slice(0, trailingZero);
      }
      decPart = trimmed;
    }
    // Remove decimal if all zeros
    if (decPart.length === 0 || /^0+$/.test(decPart)) return intPart;
    return `${intPart}.${decPart}`;
  }
  return 0;
};

export const replacePlaceholders = (str, replacements) =>
  Object.entries(replacements).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`\\$\\{${key}\\}`, "g"), value),
    str,
  );

export const joinWithAnd = (...args) => {
  const arr = Array.isArray(args[0]) ? args[0] : args;
  const filtered = arr.filter(Boolean);
  const len = filtered.length;
  if (!len) return "";
  if (len === 1) return filtered[0];
  return filtered.slice(0, -1).join(", ") + (len > 1 ? " & " + filtered[len - 1] : "");
};

export const createArray1D = (length, val = 0) => Array.from({ length }, () => val);

export const createArray2D = (rows, cols, val = 0) => Array.from({ length: rows }, () => Array(cols).fill(val));

export const getSuperscriptString = ({ text, charCode }) => {
  if (charCode !== undefined && !isNaN(charCode)) {
    return `<sup>${String.fromCharCode(charCode)}</sup>`;
  }
  return `<sup>${text}</sup>`;
};

export function formatNumberWithExponential(value, digits = 3, expThreshold = 10000000, expDigits = 6) {
  if (Math.abs(value) >= expThreshold) {
    return value.toExponential(expDigits);
  }
  return Number(value).toFixed(digits);
}

export const getLocaleDecimalSeparator = (locale = getActiveLocale()) =>
  new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === "decimal")?.value || ".";

export const localizeDecimalSeparator = (value, locale) => {
  if (typeof value !== "string") return value;
  const decimalSeparator = getLocaleDecimalSeparator(locale);
  return decimalSeparator === "." ? value : value.replace(".", decimalSeparator);
};

// Converts locale-formatted user input string to US decimal format (safe for intermediate states like "1,")
export const normalizeInputToUS = value => {
  const decimalSep = getLocaleDecimalSeparator(getActiveLocale());
  if (decimalSep !== ",") return String(value ?? "");
  return String(value ?? "")
    .replace(/\./g, "")
    .replace(",", ".");
};

// Parses a locale-formatted number string (from formatNumberInLocale) back to a JS number.
export const parseLocaleNumber = str => {
  const decSep = getLocaleDecimalSeparator(getActiveLocale());
  if (decSep === ",") {
    return parseFloat(String(str).replace(/\./g, "").replace(",", "."));
  }
  return parseFloat(String(str).replace(/,/g, ""));
};

export const formatLocalizedNumberWithExponential = (value, digits = 3, expThreshold = 10000000, expDigits = 6) => {
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }

  if (Math.abs(numericValue) >= expThreshold) {
    return localizeDecimalSeparator(numericValue.toExponential(expDigits));
  }

  return convertUptoDigits(numericValue, digits, true, { localize: true });
};

export const extractUnit = input => {
  const match = input.match(/[\d.]+\s*([a-zA-Z]+)/);
  return match ? match[1] : null;
};

// Extract the first numeric value from the string
export const extractFirstNumber = sourceString => {
  const match = sourceString.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

export const isValueEmpty = value => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value === "") ||
    (typeof value !== "string" && isNaN(value))
  )
    return true;
  return false;
};

export const isSaturationExceeded = (value, isIndex) => {
  const valueNum = Number(parseFloat(value).toFixed(2));
  if (isIndex) {
    return valueNum > SATURATION_INDEX_THRESHOLD;
  } else {
    return valueNum > SATURATION_PERCENTAGE_THRESHOLD;
  }
};

export const mergeArraysByIndex = arrays => {
  if (!arrays || !Array.isArray(arrays) || arrays.length === 0) return [];

  // Find the maximum length among all arrays
  const maxLength = Math.max(...arrays.map(arr => (Array.isArray(arr) ? arr.length : 0)));

  // Transpose the arrays, filling missing values with empty strings
  const result = Array.from({ length: maxLength }, (_, i) => arrays.map(arr => arr?.[i]));

  return result;
};

export const extractRangeBounds = ranges => {
  if (!ranges) return null;

  const { soft, hard } = ranges;
  return {
    hardMin: hard?.min ?? hard?.minValue,
    hardMax: hard?.max ?? hard?.maxValue,
    softMin: soft?.min ?? soft?.minValue,
    softMax: soft?.max ?? soft?.maxValue,
  };
};

export const isEditableInput = () => {
  const element = document.activeElement;
  const editableTags = ["INPUT", "TEXTAREA"];
  return element && (editableTags.includes(element.tagName) || element.isContentEditable);
};

/**
 * Returns the minimum available unique name by appending a numeric suffix.
 * If baseName is not already taken, it is returned as-is.
 * Otherwise the lowest unused suffix (starting at 2) is appended.
 */
export const getUniqueName = (existingNames, baseName) => {
  if (!baseName) return "";

  const namesSet = new Set(existingNames);
  if (!namesSet.has(baseName)) return baseName;

  let i = 2;
  while (namesSet.has(`${baseName} ${i}`)) i++;
  return `${baseName} ${i}`;
};

export const isZeroOrEmpty = value => {
  const normalizedValue = typeof value === "string" ? value.trim() : value;

  if (normalizedValue === undefined || normalizedValue === null || normalizedValue === "") return true;
  if (typeof normalizedValue === "number") return normalizedValue === 0;
  if (typeof normalizedValue === "string") {
    const numericValue = Number(normalizedValue);
    return !isNaN(numericValue) && numericValue === 0;
  }

  return false;
};

/**
 * Normalizes a UF calc engine response object for use in Redux.
 *
 * Performs two transformations on each key:
 *  - Capitalisation fix: keys starting with two uppercase letters (e.g. "UVDose") are
 *    lowercased at both positions ("uvDose"); keys starting with one uppercase letter
 *    (e.g. "Flux") are lowercased at the first position only ("flux").
 *    Keys that are already camelCase are left unchanged.
 *  - Null coercion: null values are replaced with the sentinel string `TBD_VALUE`
 *    so downstream consumers never have to handle null.
 *
 * @param {Object|null|undefined} payload - Raw calc engine response object (e.g. `nodeData.uf_config`).
 * @returns {Object} Sanitized object with normalised keys and no null values.
 *                   Returns an empty object if payload is falsy or not a plain object.
 */
export const sanitizeUFCalcEngineResponse = payload => {
  if (!payload || typeof payload !== "object") return {};

  const sanitizedResponse = {};

  for (const key in payload) {
    if (!Object.prototype.hasOwnProperty.call(payload, key)) continue;

    const value = payload[key];
    const firstCharCode = key.charCodeAt(0);
    const secondCharCode = key.charCodeAt(1);

    const isFirstUpperCase = firstCharCode >= 65 && firstCharCode <= 90;
    const isSecondUpperCase = secondCharCode >= 65 && secondCharCode <= 90;

    let normalizedKey = key;
    if (isFirstUpperCase && isSecondUpperCase) {
      normalizedKey = `${key[0].toLowerCase()}${key[1].toLowerCase()}${key.slice(2)}`;
    } else if (isFirstUpperCase) {
      normalizedKey = `${key[0].toLowerCase()}${key.slice(1)}`;
    }

    sanitizedResponse[normalizedKey] = value === null ? TBD_VALUE : value;
  }

  return sanitizedResponse;
};

export const getTechDisplayLabel = technology => (technology === TECHNOLOGIES.IXSD ? "IXS/D" : technology);

export const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export const pollUntilDone = async (checkFn, intervalMs = 15000) => {
  let done = false;
  let value;

  while (!done) {
    // eslint-disable-next-line no-await-in-loop
    ({ done, value } = await checkFn());

    if (!done) {
      // eslint-disable-next-line no-await-in-loop
      await wait(intervalMs);
    }
  }

  return value;
};
